import { create } from "zustand";
import { createJSONStorage, persist } from "zustand/middleware";
import type {
  Passenger,
  SearchParams,
  CouponCode,
  AvailablePaymentOption,
} from "../types";

type Store = {
  bookingStatus: "success" | "loading" | "error";
  hasMarketingConsent: boolean;
  couponCode: CouponCode;
  specialRequest: string;
  showPassengerAlert: boolean;
  paymentMethod: string;
  paymentMethodId?: string;
  // "Choose when to pay": false = pay later (charged on the due date), true =
  // pay the full amount now. Defaults to pay now, which is the option the
  // design pre-selects.
  payNowSelected: boolean;
  selectedPaymentType?: string;
  availablePaymentOption?: AvailablePaymentOption;
  search: SearchParams;
  passengers: Passenger[];
  travelAgentEmail?: string;
  saveCustomerInfo?: boolean;
  saveCreditCardInfo?: boolean;
  travelAgentIataCode?: string;
  travelAgentId?: string;
  handleAddPassengersFromForm: (data: Passenger[]) => void;
  handleAddSearchParams: (searchParams: SearchParams) => void;
  setBookingStatus: (status: "success" | "loading" | "error") => void;
  setSaveCustomerInfo: (value: boolean) => void;
  setSaveCreditCardInfo: (value: boolean) => void;
  setHasMarketingConsent: (consent: boolean) => void;
  setCouponCode: (code: CouponCode) => void;
  setSpecialRequest: (request: string) => void;
  setShowPassengerAlert: (show: boolean) => void;
  setPaymentMethod: (value: string) => void;
  setPaymentMethodId: (value: string | undefined) => void;
  setPayNowSelected: (value: boolean) => void;
  setSelectedPaymentType: (value: string | undefined) => void;
  setAvailablePaymentOption: (
    value: AvailablePaymentOption | undefined,
  ) => void;
  setTravelAgentEmail: (value: string) => void;
  setTravelAgentIataCode?: (value: string) => void;
  setTravelAgentId?: (value: string) => void;
  reset: () => void;
};

export const useCheckoutStore = create<Store>()(
  persist(
    (set, get) => ({
      bookingStatus: "loading",
      hasMarketingConsent: false,
      couponCode: {
        value: "",
        status: "idle",
      },
      specialRequest: "",
      showPassengerAlert: false,
      paymentMethod: "",
      paymentMethodId: undefined,
      payNowSelected: true,
      selectedPaymentType: undefined,
      availablePaymentOption: undefined,
      search: {
        from_path: "",
        trip_id: "",
        session_id: "",
        ref: "",
        utm_campaign: "",
        branch: "",
        mode: "",
        num_passengers: "",
        currency: "",
        start_address: "",
        start_name: "",
        flight_datetime: "",
        pickup_datetime: "",
        end_address: "",
        end_name: "",
        return_flight_datetime: "",
        return_pickup_datetime: "",
        airline: "",
        flight_number: "",
        airline2: "",
        flight_number2: "",
        partner_tracking_id: "",
        customer_special_instructions: "",
        dial_code: "",
        first_name: "",
        last_name: "",
        email: "",
        phone_number: "",
        search_id: "",
        result_id: "",
      },
      saveCustomerInfo: false,
      saveCreditCardInfo: false,
      setSaveCustomerInfo: (value) => set({ saveCustomerInfo: value }),
      setSaveCreditCardInfo: (value) => set({ saveCreditCardInfo: value }),
      passengers: [],
      handleAddPassengersFromForm: (passengers) => set({ passengers }),
      handleAddSearchParams: (searchParams) => {
        set({ search: searchParams });

        const currentSpecialRequest = get().specialRequest;
        if (
          !currentSpecialRequest &&
          searchParams.customer_special_instructions
        ) {
          set({ specialRequest: searchParams.customer_special_instructions });
        }
      },
      setBookingStatus: (status) => set({ bookingStatus: status }),
      setHasMarketingConsent: (consent) =>
        set({ hasMarketingConsent: consent }),
      setCouponCode: (value: CouponCode) =>
        set({
          couponCode: {
            value: value.value,
            status: value.status,
            message: value.message,
          },
        }),
      setTravelAgentEmail: (value: string) => set({ travelAgentEmail: value }),
      setTravelAgentIataCode: (value: string) =>
        set({ travelAgentIataCode: value }),
      setTravelAgentId: (value: string) => set({ travelAgentId: value }),
      setSpecialRequest: (request) => set({ specialRequest: request }),
      setShowPassengerAlert: (show) => set({ showPassengerAlert: show }),
      setPaymentMethod: (paymentMethod) => set({ paymentMethod }),
      setPaymentMethodId: (paymentMethodId) => set({ paymentMethodId }),
      setPayNowSelected: (payNowSelected) => set({ payNowSelected }),
      setSelectedPaymentType: (selectedPaymentType) =>
        set({ selectedPaymentType }),
      setAvailablePaymentOption: (availablePaymentOption) =>
        set({ availablePaymentOption }),
      reset: () =>
        set({
          bookingStatus: "loading",
          hasMarketingConsent: false,
          couponCode: {
            value: "",
            status: "idle",
          },
          specialRequest: "",
          showPassengerAlert: false,
          paymentMethod: "",
          paymentMethodId: undefined,
          payNowSelected: true,
          availablePaymentOption: undefined,
          selectedPaymentType: "",
          travelAgentEmail: undefined,
          travelAgentIataCode: undefined,
          travelAgentId: undefined,
          search: {
            from_path: "",
            trip_id: "",
            session_id: "",
            ref: "",
            utm_campaign: "",
            branch: "",
            mode: "",
            num_passengers: "",
            currency: "",
            start_address: "",
            start_name: "",
            flight_datetime: "",
            pickup_datetime: "",
            end_address: "",
            end_name: "",
            return_flight_datetime: "",
            return_pickup_datetime: "",
            airline: "",
            flight_number: "",
            airline2: "",
            flight_number2: "",
            partner_tracking_id: "",
            customer_special_instructions: "",
            dial_code: "",
            first_name: "",
            last_name: "",
            email: "",
            phone_number: "",
            search_id: "",
            result_id: "",
          },
          passengers: [],
        }),
    }),
    {
      name: "checkout-form",
      storage: createJSONStorage(() => window.sessionStorage),
      partialize: (state) =>
        Object.fromEntries(
          Object.entries(state).filter(
            ([key]) =>
              ![
                "couponCode",
                "saveCustomerInfo",
                "saveCreditCardInfo",
                "paymentMethodId",
                "selectedPaymentType",
              ].includes(key),
          ),
        ),
    },
  ),
);
